import { CourseMetadata, SummaryData, UserClassSettings } from "@backend-types";
import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { getClass, getClasses, GetUserInfo } from "../Helpers";
import {
  ClassExtended,
  DbUser,
  InsightsView,
  TeacherClassDetailsCourse,
  UserCtx,
} from "../interfaces";
import { UserContext } from "./UserContext";
import { Role } from "../enums";
import { useHistory, useLocation } from "react-router-dom";
import {
  ClassMap,
  addClassesToMap,
  updateClassMapWithSettings,
  getRecentlyViewedActive,
  getRecentlyViewedArchived,
  getActiveClasses,
  getActiveObservedClasses,
  getArchivedClasses,
  getArchivedObservedClasses,
  getClassesBySchoolTeacherCourse,
  getClassesProgressData,
} from "../components/utils/ClassMap";
import {
  getActiveStudents,
  getArchivedStudents,
  getStudentsProgressData,
  StudentMap,
} from "../components/utils/StudentMap";

export const InsightsContext = createContext({});

export const InsightsProvider: React.FC = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  // const { class_id, student_id } = useParams<{
  //   class_id?: string;
  //   student_id?: string;
  // }>();

  const [classId, setClassId] = useState<number | undefined>();
  const [studentId, setStudentId] = useState<string | undefined>();
  const insightsView: InsightsView =
    classId && studentId ? "student" : classId ? "class" : undefined;

  // need to extract the class ID and student ID from the route but cannot do that from insights CTX
  // as the CTX resides outside of the route
  useEffect(() => {
    function getClassIdFromPath() {
      // Matches /insights/class/{class_id} followed by any subroute or directly the end of the string
      const match = location.pathname.match(/\/insights\/class\/([^/]+)(\/|$)/);
      return match ? match[1] : undefined;
    }
    const class_id = getClassIdFromPath();
    const newClassId = Number.isNaN(Number(class_id)) ? undefined : Number(class_id);
    if (newClassId !== classId) {
      setClassId(newClassId);
      if (newClassId) {
        setLastClassId(newClassId);
      }
    }
  }, [location, classId]);

  useEffect(() => {
    function getStudentIdFromPath() {
      // Matches /insights/class/{class_id}/student/{student_id} followed by /summary or /info
      const match = location.pathname.match(
        /\/insights\/class\/[^/]+\/student\/([^/]+)\/(summary|info)/
      );
      return match ? match[1] : undefined;
    }
    const student_id = getStudentIdFromPath();
    if (studentId !== student_id) {
      setStudentId(student_id);
    }
  }, [location, studentId]);

  const userCtx = useContext(UserContext) as UserCtx;
  const [currentUser, setCurrentUser] = useState<DbUser | null>(null);

  // returns back from student mode to class mode
  const [lastClassId, setLastClassId] = useState<number | null>(null);
  // label of classes
  const [insightsDomainValue, setInsightsDomainValue] = useState<string | undefined>(
    undefined
  );
  /** Contains classes and students the teacher can access
   * Initially populated by get-current-user, later populated by insights filters */
  const [classMapProfilePopulated, setClassMapProfilePopulated] = useState(false);
  const [classMap, setClassMap] = useState<ClassMap>(new Map<number, ClassExtended>());
  const [studentMap, setStudentMap] = useState<StudentMap>(new Map<string, DbUser>());
  const schoolDataQueue = useRef<Map<number, boolean>>(new Map<number, boolean>());

  // contains class view progress calcs for the class cards
  const [classProgressDataMap, setClassProgressDataMap] = useState<
    Map<number, SummaryData>
  >(new Map<number, SummaryData>());
  const [studentProgressDataMap, setStudentProgressDataMap] = useState<
    Map<string, SummaryData>
  >(new Map<string, SummaryData>());
  const classProgressDataQueue = useRef<Map<number, boolean>>(new Map<number, boolean>());
  const studentProgressDataQueue = useRef<Map<string, boolean>>(
    new Map<string, boolean>()
  );

  // list of courses and moduleIds populated based on cached schools
  const [courseList, setCourseList] = useState<TeacherClassDetailsCourse[] | null>(null);

  // cached filtered results
  const [recentlyViewedActive, setRecentlyViewedActive] = useState<
    ClassExtended[] | null
  >(null);
  const [recentlyViewedArchived, setRecentlyViewedArchived] = useState<
    ClassExtended[] | null
  >(null);
  const [classesActive, setClassesActive] = useState<ClassExtended[] | null>(null);
  const [observingClassesActive, setObservingClassesActive] = useState<
    ClassExtended[] | null
  >(null);
  const [classesArchived, setClassesArchived] = useState<ClassExtended[] | null>(null);
  const [observingClassesArchived, setObservingClassesArchived] = useState<
    ClassExtended[] | null
  >(null);
  const [studentsActive, setStudentsActive] = useState<DbUser[] | null>(null);
  const [studentsArchived, setStudentsArchived] = useState<DbUser[] | null>(null);

  /** Contains currently selected class details */
  const [classInfo, setClassInfo] = useState<ClassExtended | undefined>(undefined);
  /** Contains currently selected user details */
  const [userInfo, setUserInfo] = useState<DbUser | undefined>(undefined);

  let queryParams = new URLSearchParams(location.search);
  const teacherId = queryParams.get("teacherId");
  const schoolId = queryParams.get("schoolId")
    ? Number(queryParams.get("schoolId"))
    : null;

  // cache current user to minimize userCtx triggers
  useEffect(() => {
    if (!currentUser && userCtx.dbUser) {
      setCurrentUser(userCtx.dbUser);
    }
  }, [currentUser, userCtx.dbUser]);

  // Run this when user class settings are changed so the
  // Objects in state receive the changes without re-querying backend
  const updateCachedClassSettings = (newSettings: UserClassSettings) => {
    setClassMap((prevClassMap) => {
      const updatedClassMap = updateClassMapWithSettings(prevClassMap, newSettings);
      const hasMapChanged = Array.from(updatedClassMap.entries()).some(
        ([key, value]) => prevClassMap.get(key) !== value
      );

      return hasMapChanged ? updatedClassMap : prevClassMap;
    });
  };

  // only use if you know the current class you're retriving this for will be in classMap
  // EG won't work well with observed students
  const getCourseIdFromClassID = useCallback(
    (classId: number): number | null => {
      return classMap.get(classId)?.course_id ?? null;
    },
    [classMap]
  );

  // get sidebar class data
  useEffect(() => {
    if (!insightsView) return;
    const fetchClassInfo = async (classId: number) => {
      try {
        let classInfo = await getClass(classId);
        setClassInfo(classInfo[0]);
      } catch (e) {
        console.log(e);
      }
    };
    if (classId && classInfo?.id !== classId) fetchClassInfo(classId);
  }, [classId, insightsView, classInfo]);

  // get sidebar student data
  useEffect(() => {
    if (!insightsView) return;
    const fetchUserInfo = async (userId: string) => {
      console.log("Fetching user info");
      try {
        let studentInfo = await GetUserInfo(userId);
        setUserInfo(studentInfo);
      } catch (e) {
        console.log(e);
      }
    };
    if (insightsView === "student" && studentId && userInfo?.id !== studentId)
      fetchUserInfo(studentId);
  }, [studentId, insightsView, userInfo]);

  // populate class and student map
  useEffect(() => {
    if (currentUser && !classMapProfilePopulated) {
      const profileClassDetails = userCtx.getProfileClassDetails;
      if (profileClassDetails) {
        const newClassMap = new Map<number, ClassExtended>(classMap);
        const newStudentMap = new Map<string, DbUser>(studentMap);
        let allClasses = [
          ...profileClassDetails.active.classes,
          ...profileClassDetails.active.observingClasses,
          ...profileClassDetails.archived.classes,
          ...profileClassDetails.archived.observingClasses,
        ];
        if (currentUser.role !== Role.teacher) {
          allClasses = [...profileClassDetails.recentlyViewed, ...allClasses];
        }
        let newClassAdded = false;
        allClasses.forEach((classInfo) => {
          if (!newClassMap.has(classInfo.id)) {
            newClassMap.set(classInfo.id, classInfo);
            newClassAdded = true;
          }
        });
        if (newClassAdded) {
          setClassMap(newClassMap);
        }

        // add the students from user profile to student map
        const activeObservingStudents = profileClassDetails.active.observingStudents;
        const archivedObservingStudents = profileClassDetails.archived.observingStudents;
        let newStudentAdded = false;
        [...activeObservingStudents, ...archivedObservingStudents].forEach((subRole) => {
          const studentObservedClassId = subRole.class_id;
          if (
            subRole.student &&
            !newStudentMap.has(`${subRole.student.id}-${studentObservedClassId}`)
          ) {
            newStudentMap.set(`${subRole.student.id}-${studentObservedClassId}`, {
              ...subRole.student,
              observer_details: subRole,
            });
            newStudentAdded = true;
          }
        });
        if (newStudentAdded) {
          setStudentMap(newStudentMap);
        }

        setClassMapProfilePopulated(true);
      }
    }
  }, [
    currentUser,
    userCtx,
    userCtx.getProfileClassDetails,
    classMap,
    studentMap,
    classMapProfilePopulated,
  ]);

  const updateSchoolDataQueue = (schoolId: number, isLoading: boolean) => {
    const currentLoadingStatus = schoolDataQueue.current.get(schoolId);
    if (currentLoadingStatus !== isLoading) {
      schoolDataQueue.current.set(schoolId, isLoading);
    }
  };

  const updateClassProgressDataQueue = (classId: number, isLoading: boolean) => {
    const currentLoadingStatus = classProgressDataQueue.current.get(classId);
    if (currentLoadingStatus !== isLoading) {
      classProgressDataQueue.current.set(classId, isLoading);
    }
  };

  const updateStudentProgressDataQueue = (studentId: string, isLoading: boolean) => {
    const currentLoadingStatus = studentProgressDataQueue.current.get(studentId);
    if (currentLoadingStatus !== isLoading) {
      studentProgressDataQueue.current.set(studentId, isLoading);
    }
  };

  const populateSchoolClasses = useCallback(
    async (schoolId: number | null, teacherId?: string | null) => {
      if (!schoolId) throw new Error("School ID required to retrieve data");
      if (!schoolDataQueue.current.get(schoolId)) {
        updateSchoolDataQueue(schoolId, true);
        let retrievedClasses = (await getClasses(
          {
            where: {
              AND: [
                ...(teacherId
                  ? [
                      {
                        teacher_id: {
                          equals: teacherId,
                        },
                      },
                    ]
                  : []),
                ...(schoolId
                  ? [
                      {
                        school_id: {
                          equals: schoolId,
                        },
                      },
                    ]
                  : []),
              ],
            },
          },
          true
        )) as ClassExtended[];
        updateSchoolDataQueue(schoolId, false);

        setClassMap((prevClassMap) => addClassesToMap(prevClassMap, retrievedClasses));
      }
    },
    [] // Remove the dependency on schoolDataQueue
  );

  useEffect(() => {
    if (!currentUser || currentUser?.role === Role.teacher) return;
    if (schoolId) {
      populateSchoolClasses(Number(schoolId), teacherId);
    }
  }, [schoolId, teacherId, currentUser, populateSchoolClasses]);

  // refresh cached class map data with filters
  useEffect(() => {
    if (!currentUser) return;
    const isTeacher = currentUser.role === Role.teacher;
    // Update recently viewed classes
    const recentlyViewedClassesActive = getRecentlyViewedActive(classMap);
    setRecentlyViewedActive(recentlyViewedClassesActive);
    const recentlyViewedClassesArchived = getRecentlyViewedArchived(classMap);
    setRecentlyViewedArchived(recentlyViewedClassesArchived);
    // Update active classes - This is used on home page for admin and supers search results
    const activeClasses = isTeacher
      ? getActiveClasses(classMap, currentUser.id)
      : getClassesBySchoolTeacherCourse(
          classMap,
          `${schoolId}`,
          teacherId,
          undefined,
          false
        );
    setClassesActive(activeClasses);

    // Update active observed classes
    const activeObservedClasses = isTeacher
      ? getActiveObservedClasses(classMap)
      : getClassesBySchoolTeacherCourse(
          getActiveObservedClasses(classMap),
          `${schoolId}`,
          teacherId,
          undefined,
          false
        );
    setObservingClassesActive(activeObservedClasses);

    // Update archived classes
    const archivedClasses = isTeacher
      ? getArchivedClasses(classMap)
      : getClassesBySchoolTeacherCourse(
          classMap,
          `${schoolId}`,
          teacherId,
          undefined,
          true
        );
    setClassesArchived(archivedClasses);

    // Update archived observed classes
    const archivedObservedClasses = getClassesBySchoolTeacherCourse(
      getArchivedObservedClasses(classMap),
      `${schoolId}`,
      teacherId,
      undefined,
      true
    );
    setObservingClassesArchived(archivedObservedClasses);
  }, [classMap, currentUser, schoolId, teacherId]);

  // update student filter cache
  // should only apply to teachers
  // in event that design team wants admins/supers to see filterable observed students
  // can use getStudentsBySchoolTeacherCourse in StudentMap
  useEffect(() => {
    if (!currentUser) return;
    const isTeacher = currentUser.role === Role.teacher;
    if (!isTeacher) return;

    // Update active observed students
    const activeObservedStudents = getActiveStudents(studentMap);
    setStudentsActive(activeObservedStudents);

    // Update archived observed students
    const archivedObservedStudents = getArchivedStudents(studentMap);
    setStudentsArchived(archivedObservedStudents);
  }, [studentMap, currentUser, schoolId, teacherId]);

  // distill cached user and class info into course list for course specific data
  useEffect(() => {
    // Extract courses from classes
    const coursesFromClassMap = Array.from(classMap.values()).map((classExtended) => ({
      course_id: classExtended.course_id ?? 1,
      course_name: classExtended.course_name ?? "",
      course_metadata: classExtended.course_metadata as CourseMetadata,
      course_modules: classExtended.course_modules?.split(",") ?? [],
    }));

    // Extract courses from students
    const coursesFromStudentMap = Array.from(studentMap.values())
      .filter(
        (student) =>
          student.observer_details &&
          student.observer_details.class &&
          student.observer_details.class.course
      )
      .map((student) => {
        const course = student.observer_details?.class?.course;
        return {
          course_id: course?.id ?? 1,
          course_name: course?.name ?? "",
          course_metadata: course?.metadata as CourseMetadata,
          course_modules:
            course?.modules?.map((module) => module.module_id.toString()) ?? [],
        };
      });

    // Combine, deduplicate, and set the courseList
    const allCourses = [...coursesFromClassMap, ...coursesFromStudentMap];
    const uniqueCourses = allCourses.filter(
      (course, index, self) =>
        index === self.findIndex((c) => c.course_id === course.course_id)
    );
    setCourseList(uniqueCourses);
  }, [classMap, studentMap]);

  // cache card progress data
  const fetchMissingClassesDataTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Store a reference to the timer ID so it can be cleared on cleanup
    const fetchMissingClassesData = async () => {
      if (courseList && classMap) {
        const missingClassesMap = new Map<number, ClassExtended>(
          Array.from(classMap.entries()).filter(
            ([classId, classExtended]) =>
              !classProgressDataMap.has(classId) &&
              !checkClassProgressLoading(classId) && // Check if the class isn't currently loading
              courseList.some((course) => course.course_id === classExtended.course_id)
          )
        );

        if (missingClassesMap.size > 0) {
          const newClassProgressDataMap = new Map<number, SummaryData>();
          const classIds = Array.from(missingClassesMap.keys());
          for (const classId of classIds) {
            updateClassProgressDataQueue(classId, true);
          }

          const fetchedClassProgressDataMap = await getClassesProgressData(
            missingClassesMap,
            courseList
          );

          fetchedClassProgressDataMap.forEach((value, key) => {
            newClassProgressDataMap.set(key, value);
            updateClassProgressDataQueue(key, false);
          });

          setClassProgressDataMap((prevClassProgressDataMap) => {
            const updatedMap = new Map(prevClassProgressDataMap);
            newClassProgressDataMap.forEach((value, key) => {
              updatedMap.set(key, value);
            });
            return updatedMap;
          });
        }
      }
    };

    // Clear any existing timer before setting a new one
    if (fetchMissingClassesDataTimer.current) {
      clearTimeout(fetchMissingClassesDataTimer.current);
    }

    // Set a new timer with a delay of 3000 milliseconds (3 seconds)
    fetchMissingClassesDataTimer.current = setTimeout(() => {
      fetchMissingClassesData();
    }, 3000);

    // Cleanup function to clear the timer when the component unmounts or dependencies change
    return () => {
      if (fetchMissingClassesDataTimer.current) {
        clearTimeout(fetchMissingClassesDataTimer.current);
      }
    };
  }, [classMap, courseList, classProgressDataMap]);

  // cache card progress data
  useEffect(() => {
    const fetchMissingStudentsData = async () => {
      if (courseList && studentMap) {
        const missingStudentsMap = new Map<string, DbUser>(
          Array.from(studentMap.entries()).filter(([studentId, student]) => {
            return (
              !studentProgressDataMap.has(studentId) &&
              !checkStudentProgressLoading(studentId) && // Check if the class isn't currently loading
              courseList.some(
                (course) =>
                  course.course_id === student.observer_details?.class?.course_id
              )
            );
          })
        );
        if (missingStudentsMap.size > 0) {
          const newStudentProgressDataMap = new Map<string, SummaryData>();
          const studentIds = Array.from(missingStudentsMap.keys());
          for (const studentId of studentIds) {
            updateStudentProgressDataQueue(studentId, true);
          }

          const fetchedStudentProgressDataMap = await getStudentsProgressData(
            missingStudentsMap,
            courseList
          );
          //console.log("fetchedclassProgressDataMap: ", fetchedclassProgressDataMap);

          fetchedStudentProgressDataMap.forEach((value, key) => {
            //console.log("fetchedclassProgressDataMap: ", value, key);
            newStudentProgressDataMap.set(key, value);
            updateStudentProgressDataQueue(key, false);
          });

          setStudentProgressDataMap((prevStudentProgressDataMap) => {
            const updatedMap = new Map(prevStudentProgressDataMap);
            newStudentProgressDataMap.forEach((value, key) => {
              updatedMap.set(key, value);
            });
            return updatedMap;
          });
        }
      }
    };

    fetchMissingStudentsData();
  }, [studentMap, courseList, studentProgressDataMap]);

  const checkSchoolClassesLoading = (schoolId: number) => {
    return schoolDataQueue.current.get(schoolId);
  };

  const checkClassProgressLoading = (classId: number) => {
    return classProgressDataQueue.current.get(classId);
  };

  const checkStudentProgressLoading = (studentId: string) => {
    return studentProgressDataQueue.current.get(studentId);
  };

  const handleGoBack = () => {
    if (lastClassId) {
      history.push(`/insights/class/${lastClassId}/summary`);
    } else {
      history.goBack();
    }
  };

  const getClassUrl = (classId: number) => {
    const defaultUrl = `/insights/class/${classId}/summary`;
    const classExtended = classMap.get(classId);
    if (!classExtended) return defaultUrl;
    if (classExtended.course_metadata?.exclude_from_insights) {
      return `/insights/class/${classId}/students`;
    } else {
      return defaultUrl;
    }
  };

  // should be using the class the student is associated to but fall back to current context classId if not found
  const getStudentUrl = (userId: string, classId: number) => {
    let studentId = userId;
    const student = studentMap.get(`${studentId}-${classId}`);
    if (student) {
      studentId = student.id;
    }
    const studentClass = student?.observer_details?.class;
    const studentClassId = classId ?? studentClass?.id;

    if (!studentId || !studentClassId) return;
    const classExtended = classMap.get(Number(studentClassId));
    if (classExtended?.course_metadata?.exclude_from_insights) {
      return `/insights/class/${studentClassId}/student/${studentId}/info`;
    } else {
      return `/insights/class/${studentClassId}/student/${studentId}/summary`;
    }
  };

  useEffect(() => {}, []);

  return (
    <InsightsContext.Provider
      value={{
        classInfo,
        updateCachedClassSettings,
        userInfo,
        recentlyViewedActive,
        recentlyViewedArchived,
        classesActive,
        observingClassesActive,
        classesArchived,
        observingClassesArchived,
        checkSchoolClassesLoading,
        studentsActive,
        studentsArchived,
        courseList,
        classProgressDataMap,
        studentProgressDataMap,
        insightsDomainValue,
        setInsightsDomainValue,
        handleGoBack,
        getCourseIdFromClassID,
        lastClassId,
        setLastClassId,
        getClassUrl,
        getStudentUrl,
        classId,
        studentId,
        insightsView,
      }}>
      {children}
    </InsightsContext.Provider>
  );
};
